<template>
  <div class="header">
    <AGBreadcrumb :items="itemsBreadCrumb" />
    <div class="d-flex align-end justify-space-between flex-wrap my-8">
      <v-row>
        <v-col class="py-0" cols="12">
          <v-progress-linear v-if="loading" color="#ECE7F5" height="48px" class="mb-2 rounded" indeterminate />
          <h2 v-else class="mb-2 header__title" :data-qa="titleDataQa">{{ title }}</h2>
          <p class="header__subtitle" :data-qa="subtitleDataQa">{{ subtitle }}</p>
        </v-col>
      </v-row>
      <div>
        <div class="d-flex flex-row-reverse" style="gap: 16px">
          <AGButton
            v-if="hasAddButton"
            type="primary"
            :left-icon="addButtonIcon"
            :disabled="disableAddButton"
            :data-qa="normalizeQa(addButtonDataQa)"
            @click="clickAdd"
          >
            {{ addButtonText }}
          </AGButton>

          <!-- Botão Configurações: simples OU menu -->
          <template v-if="showConfigButton">
            <!-- Se tiver itens, vira dropdown -->
            <v-menu
              v-if="hasConfigMenu"
              v-model="isConfigMenuOpen"
              location="bottom end"
              offset="8"
              :close-on-content-click="true"
            >
              <template #activator="{ props }">
                <AGButton
                  v-bind="props"
                  type="secondary"
                  :left-icon="configLeftIcon"
                  :right-icon="configRightIconComputed"
                  :disabled="disableConfigButton"
                  :data-qa="normalizeQa(configButtonDataQa)"
                >
                  {{ configButtonText }}
                </AGButton>
              </template>

              <v-list density="comfortable">
                <v-list-item
                  v-for="(item, idx) in configMenuItems"
                  :key="item.value ?? idx"
                  :disabled="item.disabled"
                  :data-qa="normalizeQa(item.dataQa || undefined)"
                  @click="onSelectConfig(item)"
                >
                  <template v-if="item.icon" #prepend>
                    <AGIcon size="16px" :icon="item.icon" />
                  </template>

                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Modo simples (sem menu) -->
            <AGButton
              v-else
              type="secondary"
              :left-icon="configLeftIcon"
              :right-icon="configRightIconComputed"
              :disabled="disableConfigButton"
              :data-qa="normalizeQa(configButtonDataQa)"
              @click="onClickConfig"
            >
              {{ configButtonText }}
            </AGButton>
          </template>

          <slot />
        </div>
        <UpdateInfo v-if="hasUpdateInfo" class="mt-2" :last-process-data="lastProcessData" @click="clickUpdateInfo" />
      </div>
    </div>
  </div>
</template>

<script>
import AGBreadcrumb from '@/components/common/AGBreadcrumb.vue';
import AGButton from '@/components/common/AGButton.vue';
import UpdateInfo from '@/components/common/UpdateInfo.vue';
import UtilsString from '@/utils/UtilsString';

export default {
  components: {
    UpdateInfo,
    AGButton,
    AGBreadcrumb,
  },

  emits: ['click:add', 'click:config', 'click:update-info'],

  props: {
    title: {
      type: String,
      default: '',
    },

    subtitle: {
      type: String,
      default: '',
    },

    itemsBreadCrumb: {
      required: true,
      type: Array,
      default: () => [],
    },

    hasAddButton: {
      type: Boolean,
      default: false,
    },

    addButtonText: {
      type: String,
      default: 'Adicionar',
    },

    addButtonIcon: {
      type: String,
      default: 'mdi-plus',
    },

    showConfigButton: { type: Boolean, default: false },

    configButtonText: { type: String, default: 'Configurações' },
    configLeftIcon: { type: String, default: '' },
    configRightIconClosed: { type: String, default: '' },
    configRightIconOpen: { type: String, default: '' },

    disableConfigButton: { type: Boolean, default: false },

    configMenuItems: { type: Array, default: () => [] },

    hasUpdateInfo: {
      type: Boolean,
      default: false,
    },

    lastProcessData: {
      type: String,
    },
    disableAddButton: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },
    titleDataQa: {
      type: String,
      default: undefined,
    },
    subtitleDataQa: {
      type: String,
      default: undefined,
    },
    addButtonDataQa: {
      type: String,
      default: undefined,
    },
  },

  data() {
    return {
      isConfigMenuOpen: false,
    };
  },

  computed: {
    hasConfigMenu() {
      return Array.isArray(this.configMenuItems) && this.configMenuItems.length > 0;
    },

    configRightIconComputed() {
      if (!this.showConfigButton) return '';
      // Se for menu, controla pelo estado aberto/fechado
      if (this.hasConfigMenu) {
        return this.isConfigMenuOpen
          ? this.configRightIconOpen || 'mdi-chevron-up'
          : this.configRightIconClosed || 'mdi-chevron-down';
      }
      // Se não for menu, usa o "closed" (ou nada)
      return this.configRightIconClosed || '';
    },
  },

  methods: {
    normalizeQa: UtilsString.normalizeQa,
    clickAdd(e) {
      this.$emit('click:add', e);
    },

    onClickConfig(e) {
      // Só emite no modo "simples"
      if (!this.hasConfigMenu) this.$emit('click:config', e);
    },
    onSelectConfig(item) {
      // Preferência: executa action, senão faz router push, senão só emite
      if (typeof item.action === 'function') {
        item.action();
      } else if (item.to) {
        this.$router.push(item.to);
      } else {
        this.$emit('select:config', item);
      }
      this.isConfigMenuOpen = false;
    },

    clickUpdateInfo(e) {
      this.$emit('click:update-info', e);
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  padding-top: 32px;
  .header__title {
    color: rgb(var(--v-theme-info));
    font-size: $font-size-lg;
    font-weight: $font-weight-bold;
  }
  .header__subtitle {
    color: $neutral-color-high-medium;
    font-size: $font-size-xs;
    font-weight: $font-weight-regular;
  }
}
</style>
